// External imports
import { AgmCoreModule } from '@vendor/agm-core';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import * as Sentry from '@sentry/angular';
import { Router, TitleStrategy } from '@angular/router';
import { PlaceholderModule } from 'carbon-components-angular';
import { provideLottieOptions } from 'ngx-lottie';

// Global imports
import { windowProvider, WindowToken } from 'app/helpers/windows.helpers';
import { ThemeModule } from 'app/modules/theme/theme.module';
import { AuthGuard, UnAuthGuard } from 'modules/core/guards/auth';
import { HttpService } from 'modules/core/services/api/http.service';
import { AuthenticationService } from 'modules/core/services/auth';
import { NavbarComponent } from 'modules/shared/menu/navbar.component';

// Local imports
import { AppRoutingModule } from './routing';
import { RootComponent } from './screens/root-component';
import { LoadIndicatorComponent } from './screens/root-component/components/load-indicator/load-indicator.component';
import { MigrationFileApiService } from './services/api/migration-file.service';
import { IncomingCallSocketService } from './services/sockets/incoming-call-socket.service';
import { environment } from 'environments/environment';
import { HashUrlInterceptor } from './interceptors/HashUrlInterceptor';
import { MatNativeDateModule } from '@angular/material/core';
import { RouteParamTitleStrategy } from 'modules/core/route-param-title-strategy';
import { AuthHubService } from './services/auth-hub/auth-hub.service';

export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

@NgModule({
	declarations: [RootComponent, NavbarComponent, LoadIndicatorComponent],
	bootstrap: [RootComponent],
	imports: [
		FormsModule,
		BrowserAnimationsModule,
		BrowserModule,
		MatProgressSpinnerModule,
		ToastrModule.forRoot(),
		AgmCoreModule.forRoot({
			apiKey: environment.GOOGLE_MAPS_KEY,
			libraries: ['places', 'visualization', 'drawing'],
		}),
		AppRoutingModule,
		ThemeModule,
		MatNativeDateModule,
		TranslateModule.forRoot({
			defaultLanguage: 'es',
			loader: {
				deps: [HttpClient],
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
			},
			isolate: true,
		}),
		PlaceholderModule,
	],
	providers: [
		AuthGuard,
		UnAuthGuard,
		AuthenticationService,
		HttpService,
		IncomingCallSocketService,
		MigrationFileApiService,
		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({ logErrors: !environment.production }),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		{ provide: WindowToken, useValue: windowProvider },
		{
			provide: APP_INITIALIZER,
			useFactory: (authHubService: AuthHubService) => () => authHubService.configureAuthListener(),
			deps: [AuthHubService],
			multi: true,
		},
		HashUrlInterceptor,
		provideLottieOptions({ player: () => import('lottie-web') }),
		provideHttpClient(withInterceptorsFromDi()),
		provideNgxWebstorage(
			withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
			withLocalStorage(),
			withSessionStorage(),
		),
		{
			provide: TitleStrategy,
			useClass: RouteParamTitleStrategy,
		},
	],
})
export class AppModule {}
